import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, Mic, Send, Lock, Sparkle, Star, ChevronDown, MapPin, Briefcase, Clock, Baby, Quote, ChevronLeft, ChevronRight, Check, Link2, Building2, Handshake, Landmark, TrendingUp, Zap, MoreHorizontal, type LucideIcon } from "lucide-react";
import { RequestAccessDialog } from "@/components/request-access-dialog";
import { SignInDialog } from "@/components/sign-in-dialog";
import { ContactModal } from "@/components/contact-modal";
import { PlanetMark } from "@/components/planet-mark";
import { SiteFooter } from "@/components/site-footer";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "Pyng — Know who to reach out to, and why now" },
      {
        name: "description",
        content:
          "Pyng watches the people who shape your career and tells you who to reach out to, why now, and what to say. Private by default — never shared or sold.",
      },
      { property: "og:title", content: "Pyng — Know who to reach out to, and why now" },
      {
        property: "og:description",
        content:
          "Pyng watches the people who shape your career and tells you who to reach out to, why now, and what to say. Private by default — never shared or sold.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Pyng — Know who to reach out to, and why now" },
      {
        name: "twitter:description",
        content:
          "Pyng watches the people who shape your career and tells you who to reach out to, why now, and what to say. Private by default — never shared or sold.",
      },
    ],
  }),
});

/**
 * The magic-moment story. One array drives headline, caption, and the phone
 * screen so the words never drift out of sync with the animation, and each
 * scene gets as long as its on-screen motion actually needs.
 */
const SCENES: Array<{ headline: string; caption: string; durationMs: number }> = [
  {
    headline: "Add a name. Pyng does the rest.",
    caption: "Type a name and a group — that's the whole setup.",
    durationMs: 7200,
  },
  {
    headline: "Pyng goes and finds them.",
    caption: "Scanning the open web for role, company, and history.",
    durationMs: 7200,
  },
  {
    headline: "Pyng watches. You stay present.",
    caption: "Tells you when to reach out — and what to say.",
    durationMs: 8400,
  },
];

const SCENE_DURATIONS = SCENES.map((s) => s.durationMs);

/**
 * Static mirror of the real /today feed — the same cards a member sees on
 * their phone, rebuilt with fictional people. Presentation only: nothing is
 * fetched, nothing opens.
 */
const TODAY_PREVIEW: {
  name: string;
  initials: string;
  role: string;
  vip?: boolean;
  fresh?: boolean;
  why: string;
  draft: string;
  pillIcon: LucideIcon;
  pill: string;
  ring: string;
  text: string;
  pillClass: string;
}[] = [
  {
    name: "Elena Marchetti",
    initials: "EM",
    role: "Chair & CEO · Vantage Industrial",
    vip: true,
    fresh: true,
    why: "Vantage agrees $1.1B sale — announced today",
    draft: "Saw the news about the Varex sale — congratulations. Big week for you.",
    pillIcon: Handshake,
    pill: "Deal · Today",
    ring: "ring-[color-mix(in_oklab,var(--urgency-blue)_45%,transparent)]",
    text: "text-[var(--urgency-blue)]",
    pillClass:
      "bg-[color-mix(in_oklab,var(--urgency-blue)_18%,transparent)] text-[var(--urgency-blue)]",
  },
  {
    name: "Priya Raghavan",
    initials: "PR",
    role: "Chief Financial Officer · Halcyon Group",
    fresh: true,
    why: "Named CFO of Halcyon Group yesterday",
    draft: "Saw you're stepping in as CFO — congratulations. Would love to hear…",
    pillIcon: Briefcase,
    pill: "Title · 1d",
    ring: "ring-[color-mix(in_oklab,var(--urgency-green)_45%,transparent)]",
    text: "text-[var(--urgency-green)]",
    pillClass:
      "bg-[color-mix(in_oklab,var(--urgency-green)_18%,transparent)] text-[var(--urgency-green)]",
  },
  {
    name: "Marcus Feld",
    initials: "MF",
    role: "Managing Partner · Feld Capital",
    vip: true,
    why: "6 months quiet — your longest gap with a VIP",
    draft: "Been meaning to reach out — are you still at the same desk? Coffee…",
    pillIcon: Clock,
    pill: "Quiet · 6mo",
    ring: "ring-[color-mix(in_oklab,var(--urgency-red)_75%,transparent)]",
    text: "text-[var(--urgency-red)]",
    pillClass:
      "bg-[color-mix(in_oklab,var(--urgency-red)_18%,transparent)] text-[var(--urgency-red)]",
  },
];

function TodayPreviewRow({ row }: { row: (typeof TODAY_PREVIEW)[number] }) {
  const Icon = row.pillIcon;
  return (
    <div
      className={`glass relative w-full overflow-hidden rounded-3xl border border-border/60 p-4 text-left ring-1 ${row.ring}`}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-2.5">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-[13px] font-semibold text-foreground/80"
        >
          {row.initials}
        </span>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[15px] font-semibold leading-tight">
            <span className="truncate">{row.name}</span>
            {row.vip && (
              <>
                <Star
                  aria-hidden
                  className="h-3.5 w-3.5 shrink-0 fill-[var(--accent-neon)] text-[var(--accent-neon)]"
                />
                <span className="shrink-0 text-[11px] font-semibold text-[var(--accent-neon)]">
                  VIP
                </span>
              </>
            )}
          </p>
          <p className="mt-0.5 truncate text-[12.5px] leading-snug text-muted-foreground/80">
            {row.role}
          </p>
        </div>
        {row.fresh && (
          <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-[var(--accent-neon-soft)] px-2 text-[11px] font-medium text-[var(--accent-neon-ink)]">
            New
          </span>
        )}
      </div>
      <p className={`mt-2.5 line-clamp-2 text-[14.5px] font-medium leading-snug ${row.text}`}>
        {row.why}
      </p>
      <p className="mt-2 line-clamp-1 border-l-2 border-[var(--accent-neon-soft)] pl-2.5 text-[12.5px] italic leading-snug text-muted-foreground/70">
        {row.draft}
      </p>
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`inline-flex h-7 min-w-0 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium ${row.pillClass}`}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{row.pill}</span>
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <span className="inline-flex h-8 items-center gap-1.5 rounded-full neon-outline bg-[var(--accent-neon-soft)] px-3 text-[13px] font-semibold text-[var(--accent-neon-ink)]">
            <Send className="h-3.5 w-3.5" /> Copy
          </span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--hairline-color)] text-muted-foreground">
            <MoreHorizontal className="h-4 w-4" />
          </span>
        </div>
      </div>
    </div>
  );
}

function StoplightPreview() {
  return (
    <div className="w-full max-w-sm space-y-3 text-left">
      <div className="flex items-baseline justify-between px-1">
        <p className="text-[17px] font-semibold tracking-tight text-foreground">
          5 reasons to reach out
        </p>
        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--urgency-green)]" />
          Today
        </p>
      </div>
      {TODAY_PREVIEW.map((r) => (
        <TodayPreviewRow key={r.name} row={r} />
      ))}
    </div>
  );
}

/**
 * Advances through the scene list on each scene's own duration. Returns the
 * index plus a setter so the progress segments can jump scenes on tap.
 */
function useScene(durations: number[]) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  }, []);
  useEffect(() => {
    if (reduced || paused) return;
    const id = window.setTimeout(
      () => setI((n) => (n + 1) % durations.length),
      durations[i] ?? 6000,
    );
    return () => window.clearTimeout(id);
  }, [i, paused, reduced, durations]);
  return { index: reduced ? 0 : i, setIndex: setI, reduced, setPaused };
}

const TESTIMONIALS: Array<{ tag: string; quote: string; name: string; role: string; initials: string }> = [
  {
    tag: "Quiet edge",
    quote:
      "The morning of signing, Pyng surfaced the exact line a CEO had said to me in a prior round. I used it. We closed before lunch.",
    name: "Helena M.",
    role: "Partner, M&A law firm",
    initials: "HM",
  },
  {
    tag: "Board & shareholders",
    quote:
      "A top-20 holder joined a new board. Pyng flagged it the morning it filed — before the proxy went out. That call was already booked.",
    name: "Rohan D.",
    role: "Head of Investor Relations, listed co.",
    initials: "RD",
  },
  {
    tag: "Privacy",
    quote:
      "Finally a relationship tool that lives only on my side. Nothing shared, nothing posted, nothing mined. For what I do, that's the whole point.",
    name: "James W.",
    role: "Managing Partner, family office",
    initials: "JW",
  },
];

function TestimonialsSection() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = TESTIMONIALS.length;

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || paused) return;
    const id = window.setInterval(() => setIdx((n) => (n + 1) % total), 6000);
    return () => window.clearInterval(id);
  }, [paused, total]);

  const go = (n: number) => setIdx(((n % total) + total) % total);

  return (
    <section id="testimonials" className="mt-28 w-full scroll-mt-20 sm:mt-40">
      <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">
        Loved by relationship-led people
      </p>
      <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">Quiet wins, shared privately.</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
        Real outcomes from the operators, partners, and founders who set up Pyng early.
      </p>

      <div
        className="relative mx-auto mt-10 max-w-2xl"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <div className="overflow-hidden rounded-3xl">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${idx * 100}%)` }}
          >
            {TESTIMONIALS.map((t) => (
              <article
                key={t.name}
                className="w-full shrink-0 px-1 sm:px-2"
                aria-roledescription="slide"
              >
                <div className="glass rounded-3xl p-6 text-left sm:p-8">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-primary ring-1 ring-primary/20">
                      <Sparkle className="h-2.5 w-2.5" />
                      {t.tag}
                    </span>
                    <Quote className="h-5 w-5 text-primary/40" strokeWidth={1.75} />
                  </div>
                  <p className="mt-5 text-balance text-base leading-relaxed text-foreground/90 sm:text-lg">
                    "{t.quote}"
                  </p>
                  <div className="mt-6 flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary ring-1 ring-primary/20">
                      {t.initials}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{t.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Prev / Next (desktop) */}
        <button
          type="button"
          onClick={() => go(idx - 1)}
          aria-label="Previous testimonial"
          className="absolute left-0 top-1/2 hidden -translate-x-12 -translate-y-1/2 grid-cols-1 place-items-center rounded-full glass-soft p-2 text-muted-foreground transition-colors hover:text-foreground sm:grid"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => go(idx + 1)}
          aria-label="Next testimonial"
          className="absolute right-0 top-1/2 hidden translate-x-12 -translate-y-1/2 grid-cols-1 place-items-center rounded-full glass-soft p-2 text-muted-foreground transition-colors hover:text-foreground sm:grid"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-6 flex items-center justify-center gap-1.5">
        {TESTIMONIALS.map((t, i) => (
          <button
            key={t.name}
            type="button"
            onClick={() => go(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-primary" : "w-1.5 bg-foreground/15 hover:bg-foreground/30"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function PhoneFrame({ frame }: { frame: number }) {
  return (
    <div className="relative mx-auto h-[520px] w-[260px] rounded-[2.5rem] border border-border/60 bg-[#0b0e15] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] ring-1 ring-accent-neon/15 sm:h-[560px] sm:w-[280px]">
      {/* notch */}
      <div className="absolute left-1/2 top-2 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
      {/* status bar */}
      <div className="absolute left-0 right-0 top-2 flex items-center justify-between px-7 text-[10px] text-white/70">
        <span>9:41</span>
        <span>· · ·</span>
      </div>
      {/* screen */}
      <div className="absolute inset-2 overflow-hidden rounded-[2rem]">
        {/* keyed so each scene restarts its micro-animation cleanly on every loop */}
        <div key={frame} className="h-full w-full animate-in fade-in duration-600">
          {frame === 0 && <FrameAddPerson />}
          {frame === 1 && <FrameRemembers />}
          {frame === 2 && <FramePing />}
          {frame === 3 && <FrameContextBoost />}
          {frame === 4 && <FrameBoost />}
        </div>
      </div>
    </div>
  );
}

/** Story progress: one segment per scene, filling over that scene's duration. */
function SceneProgress({
  scenes,
  index,
  reduced,
  onSelect,
}: {
  scenes: Array<{ headline: string; durationMs: number }>;
  index: number;
  reduced: boolean;
  onSelect: (i: number) => void;
}) {
  return (
    <div className="mx-auto mt-6 flex max-w-[280px] items-center gap-1.5">
      {scenes.map((s, i) => (
        <button
          key={s.headline}
          type="button"
          onClick={() => onSelect(i)}
          aria-label={`Show step ${i + 1}: ${s.headline}`}
          className="h-1 flex-1 overflow-hidden rounded-full bg-foreground/10"
        >
          <span
            key={`${i}-${index}`}
            className="block h-full rounded-full bg-accent-neon/70"
            style={
              i < index || reduced
                ? { width: "100%" }
                : i === index
                  ? { width: "100%", animation: `scene-fill ${s.durationMs}ms linear forwards` }
                  : { width: 0 }
            }
          />
        </button>
      ))}
    </div>
  );
}

function useTyped(text: string, speedMs = 108, startDelayMs = 240) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setN(text.length); return; }
    let i = 0;
    let intervalId: number | undefined;
    const startId = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        i += 1;
        setN(i);
        if (i >= text.length && intervalId !== undefined) window.clearInterval(intervalId);
      }, speedMs);
    }, startDelayMs);
    return () => {
      window.clearTimeout(startId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [text, speedMs, startDelayMs]);
  return text.slice(0, n);
}

function FrameAddPerson() {
  const first = useTyped("Elena", 100, 400);
  const firstDone = first.length >= 5;
  const last = useTyped(firstDone ? "Marchetti" : "", 90, 220);
  const lastDone = firstDone && last.length >= "Marchetti".length;
  const ctx = useTyped(lastDone ? "Board & CEOs" : "", 70, 260);
  const ctxDone = lastDone && ctx.length >= "Board & CEOs".length;
  const fields: Array<{ label: string; value: string; active: boolean; done: boolean }> = [
    { label: "First name", value: first, active: !firstDone, done: firstDone },
    { label: "Last name", value: last, active: firstDone && !lastDone, done: lastDone },
    { label: "Group", value: ctx, active: lastDone && !ctxDone, done: ctxDone },
  ];
  return (
    <div className="relative h-full w-full bg-gradient-to-b from-[#0f1422] via-[#131a2c] to-[#0a0e18] px-4 pt-10 text-white">
      <p className="text-[10px] uppercase tracking-widest text-white/50">Add to your people</p>
      <h3 className="mt-1 text-[15px] font-semibold tracking-tight">Who do you want to remember?</h3>
      <div className="mt-4 space-y-2">
        {fields.map((f) => (
          <div key={f.label} className="rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2">
            <p className="text-[9px] uppercase tracking-widest text-white/40">{f.label}</p>
            <p className="mt-0.5 text-[13px] font-medium min-h-[1.1em]">
              {f.value}
              {f.active && (
                <span className="ml-0.5 inline-block h-3.5 w-px translate-y-0.5 animate-pulse bg-accent-neon" />
              )}
            </p>
          </div>
        ))}
      </div>
      {ctxDone && (
        <div className="mt-4 flex items-center justify-center gap-1.5 rounded-full bg-accent-neon-soft px-3 py-1.5 text-[10px] text-accent-neon-ink ring-1 ring-accent-neon/40 animate-in fade-in slide-in-from-bottom-1 duration-500">
          <Check className="h-3 w-3" /> Added to Board & CEOs
        </div>
      )}
      <div className="absolute bottom-4 left-4 right-4 flex items-center gap-1.5 text-[9px] text-white/45">
        <Sparkle className="h-2.5 w-2.5 text-accent-neon" />
        Pyng will find them from here
      </div>
    </div>
  );
}

function FrameRemembers() {
  const SCRAPE_STEPS = [
    "Indexing contact…",
    "Mining the open web…",
    "Reading filings & board lists…",
    "Cross-referencing recent signals…",
    "Composing memory…",
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    const id = window.setInterval(() => setStep((s) => Math.min(s + 1, SCRAPE_STEPS.length - 1)), 1080);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="relative h-full w-full bg-[#0c111c] px-4 pt-6 text-white">
      <p className="text-[9px] uppercase tracking-widest text-white/45 text-center">Pyng is getting to know Elena</p>
      <div className="mt-4 relative mx-auto h-20 w-20">
        <div className="absolute inset-0 rounded-full border border-primary/30 animate-[spin_14s_linear_infinite]" />
        <div className="absolute inset-2 rounded-full border border-primary/40 animate-[spin_9s_linear_infinite_reverse]" />
        <div className="absolute inset-4 rounded-full border border-accent-neon/40 animate-[spin_6s_linear_infinite]" />
        <div className="absolute inset-6 rounded-full bg-gradient-to-br from-primary/80 to-primary/40 shadow-[0_0_30px_rgba(124,168,255,0.6)] animate-pulse ring-1 ring-accent-neon/50 shadow-[var(--shadow-neon-ring)]" />
        <div className="absolute inset-0 animate-[spin_14s_linear_infinite]" aria-hidden>
          <div className="absolute left-1/2 -translate-x-1/2" style={{ top: -11 }}>
            <div className="grid h-[22px] w-[22px] place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold shadow-[0_2px_10px_rgba(124,168,255,0.55)] ring-1 ring-primary/40 animate-[spin_14s_linear_infinite_reverse]">
              EM
            </div>
          </div>
        </div>
      </div>
      <ul className="mt-5 space-y-1.5">
        {SCRAPE_STEPS.slice(0, step + 1).map((label, i) => {
          const active = i === step;
          return (
            <li key={i} className="flex items-center gap-2 text-[11px] animate-in fade-in slide-in-from-bottom-1 duration-300">
              <span className={`grid h-4 w-4 place-items-center rounded-full ${active ? "bg-accent-neon-soft text-accent-neon-ink" : "bg-primary/10 text-primary/70"}`}>
                {active ? <Sparkles className="h-2.5 w-2.5 animate-pulse" /> : <Check className="h-2.5 w-2.5" />}
              </span>
              <span className={active ? "text-white/90" : "text-white/55"}>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FramePing() {
  const draft = "Congratulations on the Teledyne deal — a decisive outcome for Vantage. Would love to hear how you got it over the line.";
  // Person + signal land first, then the draft writes itself — so the caption
  // ("tells you when to reach out") reads before the message appears.
  const typed = useTyped(draft, 24, 1400);
  // The signal strip cycles the way the real person-detail carousel does.
  const SIGNALS: Array<{ icon: typeof Handshake; kind: string; when: string; text: string; source: string }> = [
    {
      icon: Handshake,
      kind: "M&A",
      when: "today",
      text: "Teledyne to acquire Varex Imaging for $1.1B",
      source: "reuters.com",
    },
    {
      icon: Landmark,
      kind: "Board appointment",
      when: "2d",
      text: "Elected to the board of Cooper Companies",
      source: "bloomberg.com",
    },
    {
      icon: TrendingUp,
      kind: "Fundraise",
      when: "4d",
      text: "Halcyon closes $480M Fund III, Vantage anchors",
      source: "wsj.com",
    },
  ];
  const [sig, setSig] = useState(0);
  useEffect(() => {
    setSig(0);
    const id = window.setInterval(() => setSig((s) => (s + 1) % 3), 2650);
    return () => window.clearInterval(id);
  }, []);
  const active = SIGNALS[sig];
  const SignalIcon = active.icon;
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0c111c] px-3.5 pt-5 text-white">
      <article className="rounded-3xl bg-white/[0.04] p-3 ring-1 ring-primary/15 text-left">
        <div className="flex flex-col items-center text-center">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/20 text-primary text-base font-semibold ring-1 ring-primary/25">
            EM
          </div>
          <h3 className="mt-2 text-[13px] font-semibold inline-flex items-center gap-1.5 leading-tight">
            Elena Marchetti
            <Star className="h-3 w-3 fill-primary text-primary" />
          </h3>
          <p className="mt-0.5 text-[10px] text-white/55">Chair &amp; CEO · Vantage Industrial (NYSE: VTG)</p>
        </div>
        {/* Signal tile — same shape as the real person detail signal strip. */}
        <div key={sig} className="mt-2.5 rounded-2xl bg-accent-neon-soft/60 px-2.5 py-2 ring-1 ring-accent-neon/30 animate-in fade-in slide-in-from-right-2 duration-400">
          <span className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-accent-neon-ink">
            <SignalIcon className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{active.kind}</span>
            <span className="ml-auto shrink-0 font-medium normal-case tracking-normal text-accent-neon-ink/70">{active.when}</span>
          </span>
          <span className="mt-1 block min-h-[2.4em] text-[10.5px] leading-snug text-white/90">
            {active.text}
            <span className="text-white/45"> — </span>
            <span className="text-accent-neon-ink underline decoration-dotted underline-offset-2">{active.source}</span>
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-center gap-1.5">
          <span className="mr-1 text-[9px] text-white/40">{sig + 1} of 3</span>
          {[0, 1, 2].map((i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === sig ? "w-3.5 bg-accent-neon" : "w-1 bg-primary/30"}`} />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-[8.5px] uppercase tracking-widest text-white/45">Next Pyng idea</p>
          <p className="text-[8.5px] uppercase tracking-widest text-white/45">Tone</p>
        </div>
        <div className="mt-1.5 border-l-2 border-primary/40 pl-2.5 text-[10.5px] leading-relaxed text-white/90 min-h-[4.5rem]">
          {typed}
          {typed.length < draft.length && (
            <span className="ml-0.5 inline-block h-3 w-px translate-y-0.5 animate-pulse bg-primary" />
          )}
        </div>
        <div className="mt-2.5">
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold ring-1 ring-accent-neon/30 shadow-[var(--shadow-neon-ring)]"
          >
            <Send className="h-3 w-3" /> Copy & open
          </button>
        </div>
      </article>
    </div>
  );
}

function FrameBoost() {
  return <FrameVoiceBoost />;
}

/**
 * Scene 4 — the real person-detail context panel: add a link and a company,
 * hit Boost, and Pyng enriches the file with affiliations and a fresh signal.
 */
function FrameContextBoost() {
  // 0 empty → 1 link added → 2 company added → 3 boosting → 4 enriched
  const [stage, setStage] = useState(0);
  useEffect(() => {
    setStage(0);
    const ids = [
      window.setTimeout(() => setStage(1), 840),
      window.setTimeout(() => setStage(2), 1920),
      window.setTimeout(() => setStage(3), 3120),
      window.setTimeout(() => setStage(4), 5520),
    ];
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, []);
  const chips: Array<{ icon: typeof Link2; label: string; at: number }> = [
    { icon: Link2, label: "vareximaging.com", at: 1 },
    { icon: Building2, label: "Cooper Companies", at: 2 },
  ];
  const found: Array<{ label: string; sub: string }> = [
    { label: "Chair, Varex Imaging", sub: "Board affiliation" },
    { label: "Director, Cooper Companies", sub: "Board affiliation" },
    { label: "1 new signal today", sub: "M&A · Reuters" },
  ];
  return (
    <div className="relative h-full w-full bg-[#0c111c] px-4 pt-4 text-white">
      <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary ring-1 ring-accent-neon/25">EM</div>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium">Elena Marchetti</p>
          <p className="truncate text-[10px] text-white/50">Chair &amp; CEO · Vantage Industrial</p>
        </div>
      </div>

      <p className="mt-3 text-[9px] uppercase tracking-widest text-white/45">Context</p>
      <div className="mt-2 space-y-1.5">
        {chips.map((c) => {
          const Icon = c.icon;
          const shown = stage >= c.at;
          return (
            <div
              key={c.label}
              className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-[11px] transition-all duration-500 ${
                shown
                  ? "border-accent-neon/30 bg-accent-neon-soft/40 text-white opacity-100"
                  : "border-white/10 bg-white/[0.03] text-white/25 opacity-60"
              }`}
            >
              <Icon className={`h-3 w-3 shrink-0 ${shown ? "text-accent-neon" : "text-white/25"}`} />
              <span className="truncate">{shown ? c.label : "Add a link or company…"}</span>
              {shown && <Check className="ml-auto h-3 w-3 shrink-0 text-accent-neon" />}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className={`mt-3 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-full text-[11px] font-semibold transition-all ${
          stage >= 3
            ? "bg-accent-neon-soft text-accent-neon-ink ring-1 ring-accent-neon/50"
            : "bg-primary text-primary-foreground ring-1 ring-accent-neon/30 shadow-[var(--shadow-neon-ring)] animate-pulse"
        }`}
      >
        <Zap className="h-3 w-3" /> {stage >= 4 ? "Boosted" : stage === 3 ? "Boosting…" : "Boost"}
      </button>

      <div className="mt-3">
        {stage === 3 && (
          <ul className="space-y-1.5 animate-in fade-in duration-300">
            {["Reading vareximaging.com…", "Cross-referencing filings…", "Matching affiliations…"].map((l, i) => (
              <li key={l} className="flex items-center gap-2 text-[10.5px] text-white/70">
                <Sparkles className="h-3 w-3 shrink-0 animate-pulse text-accent-neon" style={{ animationDelay: `${i * 200}ms` }} />
                {l}
              </li>
            ))}
          </ul>
        )}
        {stage >= 4 && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
            <p className="text-[9px] uppercase tracking-widest text-accent-neon">3 affiliations found</p>
            <div className="mt-2 space-y-1.5">
              {found.map((f) => (
                <div key={f.label} className="rounded-2xl border border-accent-neon/25 bg-white/[0.04] px-3 py-2">
                  <p className="text-[11px] font-medium text-white">{f.label}</p>
                  <p className="text-[9.5px] text-white/50">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FrameVoiceBoost() {
  // 3-stage micro-sequence: 0 button, 1 record, 2 transcribed
  const [stage, setStage] = useState(0);
  useEffect(() => {
    setStage(0);
    const t1 = window.setTimeout(() => setStage(1), 900);
    const t2 = window.setTimeout(() => setStage(2), 3200);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, []);
  return (
    <div className="relative h-full w-full bg-[#0c111c] px-4 pt-3 text-white">
      <div className="mx-auto h-1 w-10 rounded-full bg-white/20" />
      <div className="mt-3 flex items-center gap-2.5 border-b border-white/10 pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary">EM</div>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium">Boost Elena</p>
          <p className="truncate text-[10px] text-white/50">Add a note or voice memo</p>
        </div>
      </div>
      <div className="flex h-[330px] flex-col items-center justify-center">
        {stage === 0 && (
          <div className="flex w-full flex-col items-center animate-in fade-in duration-300">
            <div className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-left">
              <p className="text-[9px] uppercase tracking-widest text-white/40">Note</p>
              <p className="mt-1 text-[11px] text-white/40">Add a note…</p>
            </div>
            <button
              type="button"
              aria-hidden
              className="mt-5 grid h-16 w-16 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_0_0_10px_color-mix(in_oklab,var(--accent-neon)_22%,transparent)] animate-pulse"
            >
              <Mic className="h-6 w-6" />
            </button>
            <p className="mt-3 text-[10px] uppercase tracking-widest text-white/55">Hold to record</p>
          </div>
        )}
        {stage === 1 && (
          <div className="flex flex-col items-center animate-in fade-in zoom-in-95 duration-300">
            <p className="text-[10px] uppercase tracking-widest text-white/50">Recording</p>
            <p className="mt-1 font-mono text-2xl tabular-nums">0:14</p>
            <div className="mt-5 flex h-14 items-end gap-1">
              {[6, 14, 9, 22, 16, 28, 18, 12, 24, 10, 18, 14].map((h, i) => (
                <span
                  key={i}
                  className="w-1 rounded-full bg-accent-neon/80"
                  style={{ height: `${h * 1.4}px`, animation: `pulse 1.1s ease-in-out ${i * 80}ms infinite` }}
                />
              ))}
            </div>
            <div className="mt-5 grid h-12 w-12 place-items-center rounded-full bg-destructive text-destructive-foreground shadow-[0_0_0_8px_rgba(239,68,68,0.15)]">
              <Mic className="h-4 w-4" />
            </div>
          </div>
        )}
        {stage === 2 && (
          <div className="w-full px-1 animate-in fade-in slide-in-from-bottom-2 duration-400">
            <p className="text-[9px] uppercase tracking-widest text-white/45">Saved to Elena's profile</p>
            <div className="mt-2 rounded-2xl border border-accent-neon/30 bg-accent-neon-soft p-3">
              <div className="flex items-center gap-1.5 text-[10px] text-accent-neon-ink">
                <Sparkle className="h-3 w-3" /> Durable fact
              </div>
              <p className="mt-1.5 text-[12px] leading-snug text-white">
                "Elena wants a private intro to the Teledyne CFO."
              </p>
            </div>
            <p className="mt-3 text-center text-[9.5px] text-white/45">Pyng dropped the noise.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PhoneSection({ frame }: { frame: number }) {
  return (
    <div className="flex justify-center">
      <PhoneFrame frame={frame} />
    </div>
  );
}

function Landing() {
  const [signInOpen, setSignInOpen] = useState(false);
  const [pressOpen, setPressOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const signedIn = !authLoading && !!user;

  useEffect(() => {
    if (authLoading || user) return;
    let cancelled = false;
    void import("@capacitor/core").then(({ Capacitor }) => {
      if (!cancelled && Capacitor.isNativePlatform()) {
        void navigate({ to: "/login", replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authLoading, navigate, user]);

  const scene = useScene(SCENE_DURATIONS);
  const [requestOpen, setRequestOpen] = useState(false);
  const ctaClass =
    "inline-flex h-12 items-center gap-2 rounded-full bg-primary px-7 text-sm font-medium text-primary-foreground shadow-[0_8px_30px_-8px] shadow-primary/40 transition-transform hover:-translate-y-px";

  return (
    <main className="relative mx-auto flex min-h-screen max-w-5xl flex-col items-center px-5 text-center sm:px-6">
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
            <PlanetMark size={24} className="rounded-lg" />
            Pyng
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <button
              type="button"
              onClick={() => setPressOpen(true)}
              className="text-[12px] text-muted-foreground transition-colors hover:text-foreground sm:text-[13px]"
            >
              Press inquiry
            </button>
            {signedIn ? (
              <Link
                to="/today"
                className="inline-flex items-center justify-center rounded-full border border-border bg-card/60 px-4 py-1.5 text-[13px] font-medium text-foreground/90 transition-colors hover:border-foreground/30 hover:bg-card hover:text-foreground active:bg-accent"
              >
                Open Pyng
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setSignInOpen(true)}
                className="inline-flex items-center justify-center rounded-full border border-border bg-card/60 px-4 py-1.5 text-[13px] font-medium text-foreground/90 transition-colors hover:border-foreground/30 hover:bg-card hover:text-foreground active:bg-accent"
              >
                Sign in
              </button>
            )}
          </nav>
        </div>
      </header>
      <section className="relative flex min-h-[92svh] w-full flex-col items-center justify-center py-20">
      <div className="animate-in-fade">
        <p className="mb-6 flex items-center justify-center gap-2.5 text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground/70">
          <Lock aria-hidden size={11} strokeWidth={1.75} className="shrink-0 text-accent-neon" />
          Private relationship intelligence
        </p>
        <h1 className="mx-auto max-w-3xl text-balance text-4xl font-medium tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Know exactly who to reach out to{" "}
          <span className="relative inline-block text-accent-neon">
            today
            <span
              aria-hidden
              className="absolute -bottom-1 left-0 h-[2px] w-full rounded-full bg-accent-neon/50"
            />
          </span>
          {" "}— and why now.
        </h1>
        <p className="mx-auto mt-6 max-w-lg text-balance text-[15px] leading-relaxed text-muted-foreground sm:text-base">
          Pyng quietly watches the people who shape your career, then surfaces the right moment and a message ready to send.
        </p>
        <div className="mt-8 flex justify-center">
          <button type="button" onClick={() => setRequestOpen(true)} className={ctaClass}>
            Apply for access
          </button>
        </div>
        <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60">
          Personally reviewed · Invitations open in small groups
        </p>
      </div>
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-accent-neon/70">
        <span className="text-[10px] uppercase tracking-[0.18em]">Scroll</span>
        <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
      </div>
      </section>

      {/* Push to remember — phone mockup */}
      <section id="magic-moment" className="w-full scroll-mt-20 pt-28 pb-4 sm:pt-36">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">The magic moment</p>
        <h2 className="mt-3 min-h-[2.4em] text-balance text-3xl font-semibold tracking-tight sm:min-h-[1.2em] sm:text-4xl">
          <span key={scene.index} className="inline-block animate-in fade-in slide-in-from-bottom-1 duration-700">
            {SCENES[scene.index].headline}
          </span>
        </h2>
        <p
          key={`cap-${scene.index}`}
          className="mx-auto mt-3 min-h-[2.5em] max-w-lg text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-700 sm:min-h-0"
        >
          {SCENES[scene.index].caption}
        </p>
        <div className="mt-10">
          <PhoneSection frame={scene.index} />
        </div>
        <SceneProgress
          scenes={SCENES}
          index={scene.index}
          reduced={scene.reduced}
          onSelect={scene.setIndex}
        />
      </section>

      {/* A day with Pyng — Today preview mock */}
      <section id="today" className="mt-24 w-full scroll-mt-20 sm:mt-32">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">A morning with Pyng</p>
        <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">Three people. Thirty seconds.</h2>
        <p className="mx-auto mt-3 max-w-lg text-sm text-foreground/80">
          Real moments, not reminders. Pyng reaches out when something <em className="not-italic text-foreground">happens</em> — a new role, a move, a milestone — not when a timer goes off.
        </p>
        <div className="mt-6 flex justify-center">
          <StoplightPreview />
        </div>
      </section>

      <TestimonialsSection />

      {/* What Pyng is / is not */}
      <section id="what-pyng-is" className="mt-28 w-full scroll-mt-20 sm:mt-40">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">What Pyng is</p>
        <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">A private assistant — not a CRM.</h2>
        <div className="mx-auto mt-10 max-w-2xl border-y border-border/60 py-2 text-left">
          <div className="grid grid-cols-2 px-4 py-2 text-[11px] uppercase tracking-widest text-muted-foreground">
            <span>Not</span>
            <span className="text-accent-neon/80">Is</span>
          </div>
          {[
            ["A CRM", "A private assistant"],
            ["A dashboard", "A 30-second daily ritual"],
            ["Automation", "Drafts you approve"],
            ["Your whole address book", "The few who matter most, watched closely"],
          ].map(([a, b]) => (
            <div key={a} className="grid grid-cols-2 gap-4 border-t border-border/40 px-4 py-3 text-sm">
              <span className="text-muted-foreground line-through decoration-muted-foreground/30">{a}</span>
              <span className="text-foreground">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Trust band */}
      <section id="privacy" className="mt-20 w-full sm:mt-28">
        <a href="/privacy" className="mx-auto inline-flex items-center gap-3 border-y border-border/60 px-4 py-4 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <Lock className="h-4 w-4 text-accent-neon" strokeWidth={1.75} />
          <span><strong className="font-medium text-foreground">Private by design.</strong> Your contacts, conversations, and memories are never shared, sold, or used to train AI.</span>
          <ShieldCheck className="hidden h-4 w-4 text-accent-neon sm:block" strokeWidth={1.75} />
        </a>
      </section>

      {/* Closing CTA */}
      <section className="mt-28 w-full text-center sm:mt-40">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">Your seat</p>
        <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
          Start with the people who <span className="text-accent-neon">matter</span>.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
          Your network is your most valuable asset. Pyng keeps the few people who can change your trajectory top of mind — investors, founders, clients, mentors.
        </p>
        <div className="mt-8 flex justify-center">
          <button type="button" onClick={() => setRequestOpen(true)} className={ctaClass}>
            Apply for access
          </button>
        </div>
        <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">Applications reviewed personally</p>
      </section>
      <RequestAccessDialog open={requestOpen} onOpenChange={setRequestOpen} />
      <SignInDialog open={signInOpen} onOpenChange={setSignInOpen} />
      <ContactModal open={pressOpen} onOpenChange={setPressOpen} context="press" />
      <SiteFooter />
    </main>
  );
}
